import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { meApi } from '@/api/endpoints';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatCents } from '@/utils/money';

/*
  The fee is charged ON TOP of the stake at placement and is never refunded, so
  it is disclosed as a running TOTAL rather than itemised on the stake line
  (v0.9.1 ruling — not hidden, just not on the stake). "Turnover" here is the
  stake sum the fee was charged against, which is what makes the total legible:
  a figure with no denominator reads as an arbitrary deduction.
*/
export function ActivityCard() {
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['me', 'monthly-fee'],
    queryFn: meApi.getMonthlyFee,
    staleTime: 60_000,
  });

  return (
    <Card title="Activity statement">
      {isLoading && <p className="text-sm text-white/45">Loading…</p>}
      {isError && <p className="text-sm text-red-400">Could not load your statement.</p>}

      {data && (
        <>
          <dl className="flex flex-col text-sm">
            <Row label="Billing month" value={fmtMonth(data.billingMonth)} />
            <Row label="Turnover this month" value={formatCents(data.turnoverCents)} />
            <Row
              label="Fees paid this month"
              value={data.capCents > 0 ? `${formatCents(data.feesPaidThisMonthCents)} of ${formatCents(data.capCents)} cap` : formatCents(data.feesPaidThisMonthCents)}
            />
            <Row label="Fees paid all time" value={formatCents(data.feesPaidAllTimeCents)} last />
          </dl>
          {/* 0051: the 4% is capped per calendar month (Brisbane). The cap is
              stated as a maximum, never a minimum, and the sentence changes
              once it has been reached so "fees paid" stops looking like a
              bill that keeps growing. */}
          <p className="mt-3 text-xs text-white/35">
            {data.capCents > 0 && data.feeHeadroomCents === 0
              ? `You've reached this month's ${formatCents(data.capCents)} fee cap — no more fees on bets until next month.`
              : `Transaction fee ${(data.transactionFeeBps / 100).toFixed(2)}% of each stake, charged on top of the stake when a bet is placed${data.capCents > 0 ? `, capped at ${formatCents(data.capCents)} per calendar month` : ''}.`}
          </p>
        </>
      )}

      <Button variant="secondary" size="sm" className="mt-4 w-full" onClick={() => navigate('/bets')}>
        View bet history
      </Button>
    </Card>
  );
}

function Row({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div className={`flex items-center justify-between py-2.5 ${last ? '' : 'border-b border-white/[0.07]'}`}>
      <dt className="text-white/50">{label}</dt>
      <dd className="tabular-nums">{value}</dd>
    </div>
  );
}

/* billingMonth is 'YYYY-MM' — Date parses that as UTC midnight, which is the
   previous day in AEST, so build the label from the parts instead. */
function fmtMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return ym;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
